/* ──────────────────────────────────────────────────────────────────────
 * MergeLeadDialog — searchable, ranked merge-target picker.
 *
 * Merge under the new contract is an ARCHIVE-ONLY operation:
 *   • Source lead is archived in place and gets a pointer to the target.
 *   • Source keeps ALL of its own notes / emails / tasks / activities /
 *     timeline — nothing is moved or copied to the target.
 *   • Target only receives a single reference activity ("Duplicate lead
 *     #N archived"); its existing data is untouched.
 *   • The source is restorable via Restore — the merge can be unwound
 *     because no data was migrated.
 *
 * The confirm step makes this explicit so the operator never expects
 * data consolidation.
 * ──────────────────────────────────────────────────────────────────── */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, Stack, TextField, Typography,
} from "@mui/material";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArchiveIcon from "@mui/icons-material/ArchiveOutlined";
import {
  fetchMergeCandidates,
  type MergeCandidate, type MergeCandidateReason, type LeadThread,
} from "./leadApi";
import LeadNumberBadge from "./LeadNumberBadge";

const NAVY = "#1E4D8F";
const PURPLE = "#7c3aed";
const PURPLE_DEEP = "#5b21b6";
const INFO_FG = "#1e40af";
const INFO_BG = "#eff6ff";
const INFO_EDGE = "#bfdbfe";

interface Props {
  open: boolean;
  /** The lead that will be MERGED INTO the picked target. */
  sourceLead: LeadThread;
  onClose: () => void;
  /** Fires once the operator confirms; returns when the merge resolves so
   *  the parent can dismiss the snackbar / refetch / navigate. */
  onConfirm: (targetId: number) => Promise<void>;
}

const REASON_LABEL: Record<MergeCandidateReason, string> = {
  same_phone:      "Same phone",
  same_email:      "Same email",
  same_customer:   "Same customer",
  similar_name:    "Similar name",
  same_event_type: "Same event type",
  recent:          "Recent",
};

/* Strong (green) vs. possible (orange) vs. neutral (gray) — drives
 * the badge palette. Same vocabulary the spec asked for. */
const REASON_TONE: Record<MergeCandidateReason, "strong" | "possible" | "neutral"> = {
  same_phone:      "strong",
  same_email:      "strong",
  same_customer:   "strong",
  similar_name:    "possible",
  same_event_type: "possible",
  recent:          "neutral",
};

const TONE_STYLES: Record<"strong" | "possible" | "neutral", { fg: string; bg: string; edge: string }> = {
  strong:   { fg: "#15803d", bg: "#dcfce7", edge: "#bbf7d0" },
  possible: { fg: "#b45309", bg: "#fef3c7", edge: "#fde68a" },
  neutral:  { fg: "#475569", bg: "#f1f5f9", edge: "#e2e8f0" },
};

export default function MergeLeadDialog({ open, sourceLead, onClose, onConfirm }: Props) {
  const [input, setInput]           = useState("");
  const [options, setOptions]       = useState<MergeCandidate[]>([]);
  const [loading, setLoading]       = useState(false);
  const [selected, setSelected]     = useState<MergeCandidate | null>(null);
  // Two-step flow: false → autocomplete view; true → confirm summary view.
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Per-keystroke AbortController so a stale request can't overwrite a
  // newer one — same pattern as useLeadDuplicateCheck.
  const abortRef = useRef<AbortController | null>(null);
  const initialLoadedRef = useRef(false);

  /* ── Auto-suggest on open: fire one search="" request so the dropdown
       opens already populated with the strongest duplicate candidates. */
  useEffect(() => {
    if (!open) {
      // Reset every aspect of the state when the dialog closes so the
      // next open starts clean (no stale selection, no stale options).
      setInput("");
      setOptions([]);
      setSelected(null);
      setConfirming(false);
      setSubmitting(false);
      initialLoadedRef.current = false;
      abortRef.current?.abort();
      return;
    }
    if (initialLoadedRef.current) return;
    initialLoadedRef.current = true;

    let cancelled = false;
    setLoading(true);
    const controller = new AbortController();
    abortRef.current = controller;
    fetchMergeCandidates(sourceLead.id, "", controller.signal)
      .then((rows) => {
        if (cancelled || controller.signal.aborted) return;
        setOptions(rows);
        // Smart prefill — auto-select the highest scorer only when it's
        // a "strong" signal (phone / email / customer). Lower-confidence
        // matches are surfaced as suggestions but never auto-selected, so
        // the operator never merges into a "recent" lead by accident.
        const top = rows[0];
        if (top && top.match_reasons.some((r) => REASON_TONE[r] === "strong")) {
          setSelected(top);
        }
      })
      .catch(() => { /* silent — leave dropdown empty, user can still type */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; controller.abort(); };
  }, [open, sourceLead.id]);

  /* ── Debounced typed search ──────────────────────────────────────── */
  useEffect(() => {
    if (!open) return;
    // Skip the very first effect run after open — the auto-suggest
    // effect above already populated `options`, so we'd otherwise fire
    // an immediate duplicate request for input="".
    if (!initialLoadedRef.current) return;

    const trimmed = input.trim();
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const rows = await fetchMergeCandidates(sourceLead.id, trimmed, controller.signal);
        if (controller.signal.aborted) return;
        setOptions(rows);
      } catch (e: any) {
        if (controller.signal.aborted || e?.name === "AbortError") return;
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 350);

    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [input, open, sourceLead.id]);

  const handleConfirm = useCallback(async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await onConfirm(selected.id);
      // Parent owns close-on-success — but if it forgot, the cleanup
      // effect above will still tear down state on the next close.
    } finally {
      setSubmitting(false);
    }
  }, [selected, onConfirm]);

  const sourceLabel = sourceLead.lead_number ?? `#${sourceLead.id}`;

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ fontWeight: 700, color: NAVY, display: "flex", alignItems: "center", gap: 1 }}>
        <MergeTypeIcon sx={{ color: NAVY }} />
        {confirming ? "Archive as duplicate" : "Merge into another lead"}
      </DialogTitle>

      <DialogContent dividers>
        {!confirming && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Search by lead#, customer name, phone, email, or ID. Strong matches
              (same phone / email / customer) are surfaced first.
            </Typography>

            <Autocomplete<MergeCandidate, false, false, false>
              options={options}
              value={selected}
              onChange={(_, v) => setSelected(v)}
              inputValue={input}
              onInputChange={(_, v, reason) => {
                // Ignore "reset" events that fire after the user picks an
                // option — those would otherwise overwrite their search
                // text with the auto-generated option label.
                if (reason !== "reset") setInput(v);
              }}
              loading={loading}
              filterOptions={(x) => x} // server-side filtering
              isOptionEqualToValue={(o, v) => o.id === v.id}
              getOptionLabel={(o) =>
                `${o.lead_number ?? `#${o.id}`} · ${o.name}`
              }
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <CandidateRow candidate={option} />
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  label="Find target lead"
                  placeholder="e.g. 647-555-5555, Nirmala, DCCL260525-004"
                  size="medium"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading && <CircularProgress size={18} sx={{ mr: 1 }} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              noOptionsText={loading ? "Searching…" : "No matching leads"}
              ListboxProps={{ sx: { maxHeight: 380 } }}
            />

            {selected && (
              <Box sx={{ mt: 2, p: 1.5, borderRadius: 1.5, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: NAVY, letterSpacing: 0.5, textTransform: "uppercase", fontSize: 10 }}>
                  Target preview
                </Typography>
                <Box sx={{ mt: 0.6 }}>
                  <CandidateRow candidate={selected} compact={false} />
                </Box>
              </Box>
            )}
          </>
        )}

        {confirming && selected && (
          <Box>
            <Box sx={{
              display: "flex", gap: 1.2,
              p: 1.5, borderRadius: 1.5,
              bgcolor: INFO_BG, border: `1px solid ${INFO_EDGE}`, mb: 2,
            }}>
              <InfoOutlinedIcon sx={{ color: INFO_FG, mt: "2px" }} />
              <Box>
                <Typography sx={{ fontWeight: 700, color: INFO_FG, fontSize: 13 }}>
                  Archive {sourceLabel} as a duplicate of {selected.lead_number ?? `#${selected.id}`}?
                </Typography>
                <Typography sx={{ color: INFO_FG, fontSize: 12, mt: 0.5 }}>
                  Lead {sourceLabel} will be <b>archived in place</b> with a pointer to{" "}
                  {selected.lead_number ?? `#${selected.id}`}. Its <b>notes, emails, tasks, timeline, and
                  activities all stay on {sourceLabel}</b> &mdash; nothing is moved or copied. The target
                  lead is unchanged except for a reference entry on its timeline.
                </Typography>
                <Typography sx={{ color: INFO_FG, fontSize: 11.5, mt: 0.6, fontStyle: "italic" }}>
                  The archived lead is removed from the active pipeline but stays visible in customer
                  history and the Archived Leads view. Restore at any time to bring it back active.
                </Typography>
              </Box>
            </Box>

            <Stack direction="row" gap={1.5} alignItems="stretch">
              <SidePanel title="Will be archived" lead={sourceLead} accent={PURPLE} />
              <Box sx={{ display: "flex", alignItems: "center", color: "#64748b" }}>
                <ArchiveIcon sx={{ fontSize: 22 }} />
              </Box>
              <SidePanel title="Stays active (unchanged)" candidate={selected} accent="#15803d" />
            </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {confirming ? (
          <>
            <Button onClick={() => setConfirming(false)} disabled={submitting} sx={{ textTransform: "none" }}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={submitting || !selected}
              startIcon={submitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <ArchiveIcon />}
              sx={{ textTransform: "none", bgcolor: PURPLE, "&:hover": { bgcolor: PURPLE_DEEP } }}
            >
              {submitting ? "Archiving…" : "Archive as Duplicate"}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!selected}
              onClick={() => setConfirming(true)}
              sx={{ textTransform: "none", bgcolor: NAVY, "&:hover": { bgcolor: "#2d5f8a" } }}
            >
              Review &amp; Archive
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

/* ═════ Sub-components ════════════════════════════════════════════════ */

function CandidateRow({ candidate, compact = true }: { candidate: MergeCandidate; compact?: boolean }) {
  const created = candidate.created_at
    ? new Date(candidate.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "—";
  const eventDate = candidate.event_date
    ? new Date(candidate.event_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;

  // Sort reasons by tone — strong first so the strongest signal always
  // sits leftmost in the badge row.
  const sortedReasons = [...candidate.match_reasons].sort((a, b) => {
    const order = { strong: 0, possible: 1, neutral: 2 } as const;
    return order[REASON_TONE[a]] - order[REASON_TONE[b]];
  });

  return (
    <Box sx={{ width: "100%", py: compact ? 0.4 : 0.6 }}>
      <Stack direction="row" alignItems="center" gap={0.8} flexWrap="wrap" sx={{ mb: 0.4 }}>
        <LeadNumberBadge value={candidate.lead_number ?? `#${candidate.id}`} variant="muted" />
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY, textTransform: "capitalize" }}>
          {candidate.name}
        </Typography>
        <Box flex={1} />
        {sortedReasons.map((r) => {
          const tone = TONE_STYLES[REASON_TONE[r]];
          return (
            <Box key={r} sx={{
              fontSize: 9.5, fontWeight: 800, letterSpacing: 0.4,
              color: tone.fg, bgcolor: tone.bg, border: `1px solid ${tone.edge}`,
              borderRadius: 0.6, px: 0.6, py: 0.1, textTransform: "uppercase", whiteSpace: "nowrap",
            }}>
              {REASON_LABEL[r]}
            </Box>
          );
        })}
      </Stack>
      <Stack direction="row" gap={1.5} flexWrap="wrap" sx={{ color: "#475569", fontSize: 11.5 }}>
        {candidate.phone && <Typography variant="caption" sx={{ fontSize: 11.5 }}>📞 {candidate.phone}</Typography>}
        {candidate.email && <Typography variant="caption" sx={{ fontSize: 11.5 }}>✉ {candidate.email}</Typography>}
        {candidate.event_type && (
          <Typography variant="caption" sx={{ fontSize: 11.5, textTransform: "capitalize" }}>
            🎯 {candidate.event_type.replace(/_/g, " ")}
            {eventDate ? ` · ${eventDate}` : ""}
          </Typography>
        )}
        <Typography variant="caption" sx={{ fontSize: 11.5, textTransform: "capitalize" }}>
          Status: {candidate.status}
        </Typography>
        <Typography variant="caption" sx={{ fontSize: 11.5 }}>
          Created: {created}
        </Typography>
      </Stack>
    </Box>
  );
}

function SidePanel({
  title, lead, candidate, accent,
}: {
  title: string;
  lead?: LeadThread;
  candidate?: MergeCandidate;
  accent: string;
}) {
  // Both source (LeadThread) and target (MergeCandidate) carry the same
  // human-readable fields — coalesce to a single shape so the panel can
  // render either side from the same template.
  const view = lead
    ? {
        leadNumber: lead.lead_number ?? `#${lead.id}`,
        name:       lead.name,
        phone:      lead.phone,
        email:      lead.email,
        eventType:  lead.event_type,
        eventDate:  lead.event_date,
        status:     lead.status,
      }
    : candidate
      ? {
          leadNumber: candidate.lead_number ?? `#${candidate.id}`,
          name:       candidate.name,
          phone:      candidate.phone,
          email:      candidate.email,
          eventType:  candidate.event_type,
          eventDate:  candidate.event_date,
          status:     candidate.status,
        }
      : null;
  if (!view) return null;

  return (
    <Box sx={{
      flex: 1, p: 1.4, borderRadius: 1.5,
      bgcolor: "#fff",
      border: `1px solid ${accent}55`,
      borderLeft: `4px solid ${accent}`,
    }}>
      <Typography sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.6, color: accent, textTransform: "uppercase" }}>
        {title}
      </Typography>
      <Box sx={{ mt: 0.5 }}>
        <LeadNumberBadge value={view.leadNumber} variant="muted" />
      </Box>
      <Typography sx={{ mt: 0.4, fontWeight: 700, fontSize: 13.5, color: NAVY, textTransform: "capitalize" }}>
        {view.name}
      </Typography>
      <Divider sx={{ my: 0.8 }} />
      <Stack gap={0.3}>
        {view.phone && <Typography variant="caption" sx={{ fontSize: 11.5, color: "#475569" }}>📞 {view.phone}</Typography>}
        {view.email && <Typography variant="caption" sx={{ fontSize: 11.5, color: "#475569" }}>✉ {view.email}</Typography>}
        {view.eventType && (
          <Typography variant="caption" sx={{ fontSize: 11.5, color: "#475569", textTransform: "capitalize" }}>
            🎯 {view.eventType.replace(/_/g, " ")}
            {view.eventDate ? ` · ${view.eventDate}` : ""}
          </Typography>
        )}
        <Typography variant="caption" sx={{ fontSize: 11.5, color: "#475569", textTransform: "capitalize" }}>
          Status: {view.status}
        </Typography>
      </Stack>
    </Box>
  );
}
